import { makeAutoObservable, reaction } from 'mobx'
import { employee } from 'page/catalog/employee/store/EmployeeStore'
import { demo } from 'page/catalog/store/CatalogStore'
import { FilterStore } from 'page/catalog/store/FilterStore'
import { user } from 'page/catalog/store/UserStore'
import { Item } from 'type/Item'

export class ComboCartStore {
  private _open: boolean = false
  items: Item[] = []
  selectedTop: string | null = null
  selectedBottom: string | null = null
  private slug: string = ''
  private currentEmployeeId: string | null = null
  private currentUserId: string | null = null

  topFilters = {
    brands: new FilterStore('demo.Brand', 'brands'),
    colors: new FilterStore('demo.Color', 'colors'),
    search: '',
  }

  bottomFilters = {
    brands: new FilterStore('demo.Brand', 'brands'),
    colors: new FilterStore('demo.Color', 'colors'),
    search: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  init() {
    this.initSlug()
    this.setupUserReaction()
    this.loadFromStorage()
  }

  get open(): boolean {
    return this._open
  }

  set open(value: boolean) {
    this._open = value
  }

  private setupUserReaction() {
    reaction(() => [employee.manager_id || null, user.optional?.user_id || null],
      ([newEmployeeId, newUserId]) => {
        if (newEmployeeId !== this.currentEmployeeId || newUserId !== this.currentUserId) {
          this.onUserChange(newEmployeeId, newUserId)
        }
      },
      { fireImmediately: true },
    )
  }

  private initSlug() {
    const { stand, boutique, data } = demo.it
    const { slug } = data
    const prefix = boutique ? 'boutique' : stand ? 'stand' : 'standalone'
    this.slug = `${prefix}.${slug}`
  }

  private getStorageKey(): string {
    const employeeId = this.currentEmployeeId || 'anonymous'
    const userId = this.currentUserId || 'anonymous'
    return `combocart.${this.slug}.${employeeId}.${userId}`
  }

  private cleanupOldData() {
    if (typeof window === 'undefined') return

    try {
      const keys = Object.keys(localStorage)
      const baseSlug = this.slug.replace(/^(stand|standalone|boutique)\./, '')
      const patterns = [
        new RegExp(`^combocart\\.${baseSlug}\\.anonymous`),
        new RegExp(`^combocart\\.stand\\.${baseSlug}\\.anonymous`),
        new RegExp(`^combocart\\.standalone\\.${baseSlug}\\.anonymous`),
        new RegExp(`^combocart\\.boutique\\.${baseSlug}\\.anonymous`),
      ]

      keys.forEach(key => {
        if (patterns.some(pattern => pattern.test(key))) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.error('Failed to cleanup old data:', error)
    }
  }

  private onUserChange(newEmployeeId: string | null, newUserId: string | null) {
    if (this.currentEmployeeId || this.currentUserId) {
      this.saveToStorage()
    }

    this.currentEmployeeId = newEmployeeId
    this.currentUserId = newUserId

    if ((newEmployeeId && newEmployeeId !== 'anonymous') || (newUserId && newUserId !== 'anonymous')) {
      this.cleanupOldData()
    }

    this.loadFromStorage()
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.getStorageKey())

      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          this.items = parsed
        } else if (parsed.items) {
          this.items = Array.isArray(parsed.items) ? parsed.items : []
          this.selectedTop = parsed.selectedTop || null
          this.selectedBottom = parsed.selectedBottom || null

          if (parsed.filters) {
            this.topFilters.brands.value = parsed.filters.brands || []
            this.topFilters.colors.value = parsed.filters.colors || []
            this.topFilters.search = parsed.filters.search || ''
          }

          if (parsed.topFilters) {
            this.topFilters.brands.value = parsed.topFilters.brands || []
            this.topFilters.colors.value = parsed.topFilters.colors || []
            this.topFilters.search = parsed.topFilters.search || ''
          }

          if (parsed.bottomFilters) {
            this.bottomFilters.brands.value = parsed.bottomFilters.brands || []
            this.bottomFilters.colors.value = parsed.bottomFilters.colors || []
            this.bottomFilters.search = parsed.bottomFilters.search || ''
          }
        }
      } else {
        this.clearAll()
      }
    } catch (error) {
      console.error('Failed to load combo cart from localStorage:', error)
      this.clearAll()
    }
  }

  private saveToStorage() {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const data = {
        items: this.items,
        selectedTop: this.selectedTop,
        selectedBottom: this.selectedBottom,
        topFilters: {
          brands: this.topFilters.brands.value,
          colors: this.topFilters.colors.value,
          search: this.topFilters.search,
        },
        bottomFilters: {
          brands: this.bottomFilters.brands.value,
          colors: this.bottomFilters.colors.value,
          search: this.bottomFilters.search,
        },
      }
      localStorage.setItem(this.getStorageKey(), JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save combo cart to localStorage:', error)
    }
  }

  toggleItem(item: Item) {
    const existingIndex = this.items.findIndex(i => i.item_id === item.item_id)
    if (existingIndex >= 0) {
      this.items = this.items.filter((_, index) => index !== existingIndex)
    } else {
      this.items.push(item)
    }
    this.saveToStorage()
  }

  isItemInCart(itemId: string): boolean {
    return this.items.some(item => item.item_id === itemId)
  }

  clearCart() {
    this.items = []
    this.selectedTop = null
    this.selectedBottom = null
    this.saveToStorage()
  }

  clearAll() {
    this.items = []
    this.selectedTop = null
    this.selectedBottom = null
    this.clearFilters()
  }

  clearFilters() {
    this.topFilters.brands.value = []
    this.topFilters.colors.value = []
    this.topFilters.search = ''
    this.bottomFilters.brands.value = []
    this.bottomFilters.colors.value = []
    this.bottomFilters.search = ''
    this.saveToStorage()
  }

  clearTopFilters() {
    this.topFilters.brands.value = []
    this.topFilters.colors.value = []
    this.topFilters.search = ''
    this.saveToStorage()
  }

  clearBottomFilters() {
    this.bottomFilters.brands.value = []
    this.bottomFilters.colors.value = []
    this.bottomFilters.search = ''
    this.saveToStorage()
  }

  setTopSearch(value: string) {
    this.topFilters.search = value
    this.saveToStorage()
  }

  setBottomSearch(value: string) {
    this.bottomFilters.search = value
    this.saveToStorage()
  }

  removeItem(itemId: string) {
    this.items = this.items.filter(item => item.item_id !== itemId)

    if (this.selectedTop === itemId) {
      this.selectedTop = null
    }
    if (this.selectedBottom === itemId) {
      this.selectedBottom = null
    }

    if (this.selectedTop && !this.items.some(item => item.item_id === this.selectedTop)) {
      this.selectedTop = null
    }
    if (this.selectedBottom && !this.items.some(item => item.item_id === this.selectedBottom)) {
      this.selectedBottom = null
    }

    this.saveToStorage()
  }

  get count(): number {
    return this.items.length
  }

  get topItems(): Item[] {
    return this.items.filter(item => item.dress_shape_type === 'top')
  }

  get bottomItems(): Item[] {
    return this.items.filter(item => item.dress_shape_type === 'bottom')
  }

  selectTop(itemId: string) {
    this.selectedTop = itemId
    this.saveToStorage()
  }

  selectBottom(itemId: string) {
    this.selectedBottom = itemId
    this.saveToStorage()
  }

  get selectedTopItem(): Item | null {
    return this.items.find(item => item.item_id === this.selectedTop) || null
  }

  get selectedBottomItem(): Item | null {
    return this.items.find(item => item.item_id === this.selectedBottom) || null
  }

  get canCombine(): boolean {
    const hasSelectedTop = this.selectedTop !== null && this.items.some(item => item.item_id === this.selectedTop)
    const hasSelectedBottom = this.selectedBottom !== null && this.items.some(item => item.item_id === this.selectedBottom)
    return hasSelectedTop && hasSelectedBottom
  }

  get currentEmployeeIdValue(): string | null {
    return this.currentEmployeeId
  }

  get currentUserIdValue(): string | null {
    return this.currentUserId
  }

  get storageKey(): string {
    return this.getStorageKey()
  }

}

export const comboCartStore = new ComboCartStore() 
